import { User as Auth0User } from '@auth0/auth0-react';
import { User } from '../store/slices/authSlice';
import { STORAGE_KEYS } from './storageKeys';

// Global flag to indicate authentication failure
let authFailedFlag = false;

/**
 * Sets the authentication failed flag
 * @param value - Boolean value to set the flag
 */
export const setAuthFailed = (value: boolean): void => {
  authFailedFlag = value;
};

/**
 * Gets the current value of the authentication failed flag
 * @returns The current value of the authentication failed flag
 */
export const getAuthFailed = (): boolean => {
  return authFailedFlag;
};

// Define interfaces for Auth0 user and metadata
export interface Auth0UserWithRoles extends Auth0User {
  [key: string]: unknown;
  nickname?: string;
  given_name?: string;
  family_name?: string;
  sub: string;
  email: string;
}

export interface AppMetadata {
  company_id?: string;
  company_name?: string;
  [key: string]: unknown;
}

export interface Auth0UserWithMetadata extends Auth0UserWithRoles {
  company_id?: string;
  company_name?: string;
  app_metadata?: AppMetadata;
}

interface DecodedTokenRole {
  id: string;
  name: string;
  description?: string;
  is_system_role?: boolean;
  role_type?: string;
}

interface DecodedTokenPermission {
  id: string;
  name: string;
  action: string;
  entity_type: string;
}

interface DecodedTokenCompany {
  id: string;
  company: string;

}

interface DecodedToken {
  roles?: DecodedTokenRole[];
  permissions?: DecodedTokenPermission[];
  company?: DecodedTokenCompany;
  [key: string]: unknown;
}

/**
 * Helper function to extract company ID from Auth0 token claims
 * Consolidated from multiple implementations across the codebase
 */
export const extractCompanyIdFromToken = (auth0User: Auth0UserWithRoles): string | undefined => {
  // The namespace should match what's configured in Auth0
  const namespace = import.meta.env.VITE_AUTH0_AUDIENCE;
  
  // Try to extract company_id from different possible locations in the token
  // Option 1: From a custom claim with namespace
  const companyIdFromNamespace = ((auth0User as unknown) as { [key: string]: string })[`${namespace}/company_id`];
  if (companyIdFromNamespace) return companyIdFromNamespace;
  
  // Option 2: From app_metadata if available
  const userWithMetadata = auth0User as unknown as Auth0UserWithMetadata;
  if (userWithMetadata.app_metadata?.company_id) return userWithMetadata.app_metadata.company_id;
  
  // Option 3: From a top-level claim
  if (userWithMetadata.company_id) return userWithMetadata.company_id;
  
  return undefined;
};

/**
 * Maps Auth0 user to application user
 * Consolidated from multiple implementations across the codebase
 */
export const mapAuth0UserToAppUser = (auth0User: Auth0UserWithRoles, userRole: string): User => {
  // Extract company information
  const companyId = extractCompanyIdFromToken(auth0User);
  
  // Extract company name if available
  const userWithMetadata = auth0User as unknown as Auth0UserWithMetadata;
  const companyName = userWithMetadata.company_name || userWithMetadata.app_metadata?.company_name;
  
  return {
    id: auth0User.sub,
    username: auth0User.nickname || auth0User.email,
    email: auth0User.email,
    role: userRole,
    firstName: auth0User.given_name,
    lastName: auth0User.family_name,
    companyId,
    companyName,
  };
};

/**
 * Utility function to handle token storage consistently
 */
export const storeAccessToken = (token: string): void => {
  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
};

/**
 * Utility function to retrieve stored access token
 */
export const getStoredAccessToken = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
};

/**
 * Utility function to clear stored access token
 */
export const clearStoredAccessToken = (): void => {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
};

/**
 * Decodes JWT token
 */
export function decodeJwt(token: string): DecodedToken | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

/**
 * Extracts user details from the access token
 */
export function extractUserDetailsFromToken(accessToken: string): {
  roles: DecodedTokenRole[];
  permissions: DecodedTokenPermission[];
  company?: string;
} {
  const decodedToken = decodeJwt(accessToken);

  if (!decodedToken) {
    return { roles: [], permissions: [] };
  }

  return {
    roles: decodedToken.roles || [],
    permissions: decodedToken.permissions || [],
    company: typeof decodedToken.company === 'string' ? decodedToken.company : undefined,
  };
}

